import React, { useState } from 'react'
import {
  Button,
  Col,
  Container,
  Content,
  Page,
  Paragraph,
  Row,
  Section,
  Title,
} from '@ix/ix-ui'
import { useAuth } from '../../../helpers/auth'
import { Controller, useForm } from 'react-hook-form'
import { format } from 'date-fns'
import { CustomDatePicker } from '../../../components/General/SPUDDatePicker'
import 'react-datepicker/dist/react-datepicker.css'
import MaskedTextInput from 'react-text-mask'
import { SPUDInputField } from '../../../helpers/record'
import ReactSwitch from 'react-switch'
import toast, { Toaster } from 'react-hot-toast'
import SPUDToastNotification from '../../../components/General/SPUDToastNotification'
import styled from 'styled-components'
import { requestReport } from '../../../services/spud.service'

type ReportFormData = {
  fromDate: Date | null
  toDate: Date | null
  reportType: 'dataset' | 'qcoss' | 'dcj'
  emailTo: string
  regenerateAllFiles: boolean
}

const REPORT_TYPE_OPTIONS = [
  { value: 'dataset', label: 'Dataset' },
  { value: 'qcoss', label: 'QCOSS' },
  { value: 'dcj', label: 'DCJ' },
]

const FormField = styled.div`
  margin-bottom: 1.5em;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5em;
  }
`

const StyledSelect = styled.select`
  border: 1px solid #3a8ae8;
  border-radius: 3px;
  padding: 10px;
  min-width: 200px;
  background: none;
`

const SwitchLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
`

const ErrorText = styled.span`
  color: red;
  font-size: 0.85em;
  display: block;
  margin-top: 0.25em;
`

function ReportsPage () {
  const { userRole, user } = useAuth()
  const [loading, setLoading] = useState(false)

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportFormData>({
    defaultValues: {
      reportType: 'dataset',
      regenerateAllFiles: false,
      emailTo: user.email,
      fromDate: null,
      toDate: null,
    },
  })

  const onSubmit = async (data: ReportFormData) => {
    if (!data.fromDate || !data.toDate) return

    const formatDate = (d: Date) => format(d, 'yyyy-MM-dd')

    try {
      setLoading(true)
      await requestReport({
        report_type: data.reportType,
        email_to: data.emailTo,
        start_date: formatDate(data.fromDate),
        end_date: formatDate(data.toDate),
        ...(data.regenerateAllFiles ? { regenerate_all_files: true } : {}),
      })
      toast.custom(
        <SPUDToastNotification
          title="Report requested"
          message="The report has been queued successfully."
          success
        />,
      )
    } catch (error) {
      toast.custom(
        <SPUDToastNotification
          title="Error requesting report"
          message="Failed to request the report. Please check your inputs and try again."
          error
        />,
      )
    } finally {
      setLoading(false)
    }
  }

  if (userRole !== 'Administrator') {
    return (
      <Page>
        <Content>
          <Row style={{ padding: '1em 0 0 2em' }}>
            <Col>
              <Title level={1}>Unauthorized Access</Title>
            </Col>
          </Row>
          <Row>
            <Col>
              <Section>You are unauthorized to access this page.</Section>
            </Col>
          </Row>
        </Content>
      </Page>
    )
  }

  return (
    <Page>
      <Content>
        <Row style={{ padding: '1em 0 0 2em' }}>
          <Col>
            <Title level={1}>ISS Search reports</Title>
          </Col>
        </Row>
        <Row>
          <Col>
            <Section>
              <Container style={{ background: '#fff', padding: '1.5em' }}>
                <Row>
                  <Col>
                    <Paragraph>
                      Use this form to request generation of ISS Search reports.
                      Once you submit the form, the report will be queued for generation
                      and you will receive an email when its ready for download.
                    </Paragraph>
                  </Col>
                </Row>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Row>
                    <Col>
                      <FormField>
                        <label htmlFor="fromDate">From date (inclusive)</label>
                        <Controller
                          name="fromDate"
                          control={control}
                          rules={{ required: 'From date is required' }}
                          render={({ field }) => (
                            <CustomDatePicker
                              fullWidth={false}
                              selected={field.value ? new Date(field.value) : null}
                              dateFormat="dd/MM/yyyy"
                              onChange={field.onChange}
                              placeholderText="dd/mm/yyyy"
                              customInput={
                                <MaskedTextInput
                                  id="fromDate"
                                  type="text"
                                  mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                />
                              }
                              minDate={new Date(2000, 0, 1)}
                              isClearable
                            />
                          )}
                        />
                        {errors.fromDate && <ErrorText>{errors.fromDate.message}</ErrorText>}
                      </FormField>
                    </Col>
                    <Col>
                      <FormField>
                        <label htmlFor="toDate">To date (inclusive)</label>
                        <Controller
                          name="toDate"
                          control={control}
                          rules={{ required: 'To date is required' }}
                          render={({ field }) => (
                            <CustomDatePicker
                              fullWidth={false}
                              selected={field.value ? new Date(field.value) : null}
                              dateFormat="dd/MM/yyyy"
                              onChange={field.onChange}
                              placeholderText="dd/mm/yyyy"
                              customInput={
                                <MaskedTextInput
                                  type="text"
                                  id="toDate"
                                  mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                                />
                              }
                              minDate={new Date(2000, 0, 1)}
                              isClearable
                            />
                          )}
                        />
                        {errors.toDate && <ErrorText>{errors.toDate.message}</ErrorText>}
                      </FormField>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormField>
                        <label htmlFor="reportType">Report type</label>
                        <StyledSelect
                          id="reportType"
                          {...register('reportType', { required: true })}
                        >
                          {REPORT_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </StyledSelect>
                      </FormField>
                    </Col>
                    <Col>
                      <FormField>
                        <SPUDInputField
                          label="Email to"
                          id='emailTo'
                          type="email"
                          readOnly
                          {...register('emailTo')}
                        />
                      </FormField>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormField>
                        <label htmlFor='regenerateAllFiles'>Regenerate all files</label>
                        <Controller
                          name="regenerateAllFiles"
                          control={control}
                          render={({ field }) => (
                            <SwitchLabel>
                              <ReactSwitch
                                id='regenerateAllFiles'
                                checked={field.value}
                                onChange={field.onChange}
                              />
                              <span>{field.value ? 'Yes' : 'No'}</span>
                            </SwitchLabel>
                          )}
                        />
                      </FormField>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Button primary type="submit" disabled={loading}>
                        {loading ? 'Requesting...' : 'Generate report'}
                      </Button>
                    </Col>
                  </Row>
                </form>
              </Container>
            </Section>
          </Col>
        </Row>
        <Toaster />
      </Content>
    </Page>
  )
}

export default ReportsPage
